import { TiptapTransformer } from "@hocuspocus/transformer";
import * as Sentry from "@sentry/react";
import { useEditorState } from "@tiptap/react";
import classnames from "classnames";
import _ from "lodash";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useCommentStore } from "../../../stores/commentStore";
import { CommentCountBadge } from "./CommentCountBadge";
import { useProjectStore } from "../../../stores/projectStore";
import { useStoryBibleStore } from "../../../stores/storyBibleStore";
import { useSudowriteStore } from "../../../stores/sudowriteStore";
import IconButton from "../../IconButton";
import { HighlightColorPicker } from "./HighlightColorPicker";
import { LinkPopover } from "./LinkPopover";
import OutlineSectionSelector from "../../StoryBible/OutlineSectionSelector";
import { useStructuredOutlineStore } from "../../StoryBible/StructuredOutline/StructuredOutlineStore";
import { createDocumentSnapshot } from "../documentSnapshots";
import History from "../History";
import { hasExtraSpacing, removeExtraSpacing } from "../../../lib/tipTapExtensions/removeExtraSpacing";
import { sendAppEvent } from "../../../Utils";
import { StyledContainer } from "./styles";
import { getVisibleToolbarItemCount, startsToolbarGroup } from "./toolbarLayout";
import StickyNoteAnchor from "../../StickyTour/StickyNoteAnchor";
import { AnchorIds, TourSignal } from "../../StickyTour/TourTypes";
import { useStickyTourStore } from "../../../stores/stickyTourStore";
import { MenuPopoverItem, SudoMenuButton } from "../../../sudokit/components/SudoMenuButton";
import { useToast } from "../../ToastWrapper";

/**
 * This is the EditorToolbar component for the Tiptap editor, which is used to apply formatting to text
 * @param {Object} editor the Tiptap object
 * @param {Boolean} leftBarCollapsed whether the left bar is collapsed
 */
function EditorToolbar({
  editor, //
  editorReady,
  onUndo,
  onRedo,
  leftBarCollapsed,
  documentId,
  editorComputedStyles,
  editorToolbarRef,
  toggleFindAndReplaceOpen,
  setRightBarCollapsed,
  hideLinkingMenu,
  ydoc,
  toggleSpellCheck,
  onEditorLayoutChange,
}) {
  const [showHistory, setShowHistory] = useState(false);
  const [isCreatingSnapshot, setIsCreatingSnapshot] = useState(false);
  const [showHiddenItems, setShowHiddenItems] = useState(false);
  const formatToolbarRef = useRef(null);
  const [formatToolbarWidth, setFormatToolbarWidth] = useState(0);
  const [showRemoveEmptyLinesMenu, setShowRemoveEmptyLinesMenu] = useState(false);
  const [isSchedulingProjectCleanup, setIsSchedulingProjectCleanup] = useState(false);
  const { setToast } = useToast();
  const project = useProjectStore((state) => state.project);
  const pendingFrontendSpacingCleanup = useProjectStore((state) => state.pendingFrontendSpacingCleanup);
  const clearPendingFrontendSpacingCleanup = useProjectStore((state) => state.clearPendingFrontendSpacingCleanup);
  const markSpacingCleanupDocumentLocallyResolved = useProjectStore((state) => state.markSpacingCleanupDocumentLocallyResolved);
  const structuredOutline = useStructuredOutlineStore((state) => state.outline);
  const subscription = useSudowriteStore((state) => state.subscription);
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);
  const editorOptions = useSudowriteStore((state) => state.editorOptions);
  const spellCheckEnabled = editorOptions?.spellCheckEnabled || false;
  const grammarCheckEnabled = editorOptions?.grammarCheckEnabled || false;

  // Formatting can change without moving the selection. Subscribe to editor
  // transactions instead of waiting for a parent prop change to refresh colors.
  const formatState = useEditorState({
    editor,
    selector: ({ editor: currentEditor }) => ({
      bold: !!currentEditor?.isActive("bold"),
      italic: !!currentEditor?.isActive("italic"),
      underline: !!currentEditor?.isActive("underline"),
      strike: !!currentEditor?.isActive("strike"),
      bulletList: !!currentEditor?.isActive("bulletList"),
      taskList: !!currentEditor?.isActive("taskList"),
      h1: !!currentEditor?.isActive("heading", { level: 1 }),
      h2: !!currentEditor?.isActive("heading", { level: 2 }),
      h3: !!currentEditor?.isActive("heading", { level: 3 }),
    }),
  });
  const isBody = !formatState.h1 && !formatState.h2 && !formatState.h3;

  const mutateComments = useCommentStore((state) => state.mutateComments);
  /*
   * History needs two store operations in addition to refetching rows: protect the replacement
   * transaction from normal annotation-removal side effects, then refresh which anchors exist.
   * Refetching comments alone cannot restore deleted rows or update a silent editor index.
   */
  const refreshDocumentAnnotationIndex = useCommentStore((state) => state.refreshDocumentAnnotationIndex);
  const withSuppressedMarkRemovalSideEffects = useCommentStore(
    (state) => state.withSuppressedMarkRemovalSideEffects,
  );

  const handleHistoryRestore = async (contents) => {
    setIsCreatingSnapshot(true);
    try {
      // Prefer yDoc if available; fallback to editor JSON on any error
      const yDocRef = ydoc || editor?.storage?.collaboration?.document || editor?.extensionStorage?.collaboration?.document;
      if (yDocRef) {
        try {
          const snapshotContent = TiptapTransformer.fromYdoc(yDocRef);
          await createDocumentSnapshot(documentId, snapshotContent);
        } catch (_err) {
          await yDocFallbackSnapshot(editor);
        }
      } else {
        await yDocFallbackSnapshot(editor);
      }

      /*
       * The pre-restore snapshot above saves the state being left. setContent below replaces the
       * whole ProseMirror document, which can look to the annotation plugin like deleting many
       * ranges. Suppress only those cleanup side effects, then refresh the index explicitly because
       * TipTap setContent does not emit update by default. Refresh precedes the row refetch so the
       * new list is interpreted against the restored manuscript. Ordinary editing outside this
       * wrapper is unchanged; other writable tabs are not protected by this local guard.
       */
      withSuppressedMarkRemovalSideEffects(() => {
        editor.commands.setContent(contents);
      });
      // TipTap setContent suppresses the update event by default. Refresh from
      // the restored document before refetching comments against its marks.
      refreshDocumentAnnotationIndex();
      mutateComments(undefined, { historyRestore: true });
      requestAnimationFrame(() => {
        onEditorLayoutChange?.();
      });
    } finally {
      setIsCreatingSnapshot(false);
    }
  };

  const outlineObject = useStoryBibleStore((state) => state.outlineObject);
  const proseGeneratorActive = useStoryBibleStore((state) => state.proseGeneratorActive);
  const setProseGeneratorActive = useStoryBibleStore((state) => state.setProseGeneratorActive);
  const setChapterEditorActive = useStoryBibleStore((state) => state.setChapterEditorActive);
  const storyBibleActive = useStoryBibleStore((state) => state.isActive);

  const columnWidths = useProjectStore((state) => state.columnWidths);
  const setColumnWidths = useProjectStore((state) => state.setColumnWidths);
  const collapsed = columnWidths.center.collapsed;

  // The flex row receives the space left after fixed actions (including a
  // variable-width comment badge/section selector). Observe that actual space,
  // not the saved column width, which also includes the scrollbar and padding.
  useLayoutEffect(() => {
    const row = formatToolbarRef.current;
    if (!row || collapsed) return;
    const measure = () => setFormatToolbarWidth(row.getBoundingClientRect().width);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    return () => observer.disconnect();
  }, [editor, collapsed]);

  const [isExpanding, setIsExpanding] = useState(false);
  const expandTimeoutRef = useRef(null);

  const handleCollapseToggle = useCallback(() => {
    setColumnWidths((draft) => {
      draft.center.collapsed = !draft.center.collapsed;
    });
    setIsExpanding(true);

    // Clear any existing timeout
    if (expandTimeoutRef.current) {
      clearTimeout(expandTimeoutRef.current);
    }

    // Set a new timeout for when the animation should be finished
    expandTimeoutRef.current = setTimeout(() => {
      setIsExpanding(false);
    }, 300); // duration of expand/collapse animation
  }, [setColumnWidths]);

  const hasAccessToCleanCopy = subscription?.hasAccessToCleanCopy;
  const hasProjectCleanup = !!project?.spacingCleanupJob;

  const scheduleProjectCleanup = async () => {
    if (!project?.id || !documentId || isSchedulingProjectCleanup || hasProjectCleanup) return;
    setIsSchedulingProjectCleanup(true);
    try {
      // The open document is already live in the editor, so clean it here and
      // tell the worker to skip it.
      removeExtraSpacing(editor);
      await project.startSpacingCleanup({ currentDocumentId: documentId });
      setShowRemoveEmptyLinesMenu(false);
      sendAppEvent("Clean Up Spacing Project Scheduled", { projectId: project.id, documentId });
    } catch (error) {
      Sentry.captureException(error);
      setToast("Couldn’t start project cleanup. Try again.");
    } finally {
      setIsSchedulingProjectCleanup(false);
    }
  };

  const startedFrontendRetryRef = useRef(null);
  useEffect(() => {
    const action = pendingFrontendSpacingCleanup;
    if (
      !action ||
      !editor ||
      !editorReady ||
      action.projectId !== project?.id ||
      action.documentId !== documentId
    ) return;
    if (action.jobId !== project.spacingCleanupJob?.jobId) return;

    const actionKey = `${action.jobId}:${action.documentId}`;
    if (startedFrontendRetryRef.current === actionKey) return;
    try {
      removeExtraSpacing(editor);
      markSpacingCleanupDocumentLocallyResolved(action);
      startedFrontendRetryRef.current = actionKey;
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      clearPendingFrontendSpacingCleanup(action);
    }
  }, [
    clearPendingFrontendSpacingCleanup,
    documentId,
    editor,
    editorReady,
    markSpacingCleanupDocumentLocallyResolved,
    pendingFrontendSpacingCleanup,
    project,
  ]);

  const rightToolbarItems = [
    {
      id: "searchAndReplace",
      component: (
        <IconButton
          src="/assets/editor-toolbar/search.svg"
          onClick={() => toggleFindAndReplaceOpen()}
          smartFill={false}
          className="toolbar-icon"
          ariaLabel="Find and Replace"
          hoverTipText="Find and Replace"
        />
      ),
    },
    {
      id: "spellcheck",
      component: (
        <IconButton
          src="/assets/editor-toolbar/spellcheck.svg"
          onClick={() => toggleSpellCheck?.()}
          smartFill={false}
          className={classnames({
            active: spellCheckEnabled || grammarCheckEnabled,
            spellcheck: true,
            "toolbar-icon": true,
          })}
          ariaLabel="Spellcheck"
          hoverTipText="Spellcheck"
        />
      ),
    },
    {
      id: "undo",
      component: (
        <IconButton
          src="/assets/editor-toolbar/undo.svg"
          onClick={() => {
            if (typeof onUndo === "function") {
              onUndo();
              return;
            }
            editor.chain().focus().undo().run();
          }}
          smartFill={false}
          className="toolbar-icon"
          ariaLabel="Undo"
          hoverTipText="Undo"
        />
      ),
    },
    {
      id: "redo",
      component: (
        <IconButton
          src="/assets/editor-toolbar/redo.svg"
          onClick={() => {
            if (typeof onRedo === "function") {
              onRedo();
              return;
            }
            editor.chain().focus().redo().run();
          }}
          smartFill={false}
          className="toolbar-icon"
          ariaLabel="Redo"
          hoverTipText="Redo"
        />
      ),
    },
    {
      id: "bold",
      component: (
        <IconButton
          src="/assets/editor-toolbar/Bold.svg"
          onClick={(e) => editor.chain().focus().toggleBold().run()}
          className={classnames({ active: formatState.bold, bold: true, "toolbar-icon": true })}
          ariaLabel="Bold"
          aria-pressed={formatState.bold}
          hoverTipText="Bold"
        />
      ),
    },
    {
      id: "italic",
      component: (
        <IconButton
          src="/assets/editor-toolbar/italics.svg"
          onClick={(e) => editor.chain().focus().toggleItalic().run()}
          className={classnames({ active: formatState.italic, italic: true, "toolbar-icon": true })}
          ariaLabel="Italic"
          aria-pressed={formatState.italic}
          hoverTipText="Italic"
        />
      ),
    },
    {
      id: "underline",
      component: (
        <IconButton
          src="/assets/editor-toolbar/underline.svg"
          onClick={(e) => editor.chain().focus().toggleUnderline().run()}
          className={classnames({ active: formatState.underline, underline: true, "toolbar-icon": true })}
          ariaLabel="Underline"
          aria-pressed={formatState.underline}
          hoverTipText="Underline"
        />
      ),
    },
    {
      id: "strikethrough",
      component: (
        <IconButton
          src="/assets/editor-toolbar/strikethrough.svg"
          onClick={(e) => editor.chain().focus().toggleStrike().run()}
          className={classnames({ active: formatState.strike, strike: true, "toolbar-icon": true })}
          ariaLabel="strikethrough"
          aria-pressed={formatState.strike}
          hoverTipText="Strikethrough"
        />
      ),
    },
    {
      id: "link",
      component: <LinkPopover editor={editor} />,
    },
    ...(subscription?.hasAccessToTextHighlight
      ? [
          {
            id: "highlight",
            component: <HighlightColorPicker editor={editor} />,
          },
        ]
      : []),
    {
      id: "bullets",
      group: "lists",
      component: (
        <IconButton
          src="/assets/editor-toolbar/bullets.svg"
          onClick={(e) => editor.chain().focus().toggleBulletList().run()}
          className={classnames({ active: formatState.bulletList, "toolbar-icon": true, "list-icon": true })}
          ariaLabel="Unordered List"
          aria-pressed={formatState.bulletList}
          hoverTipText="Bullet List"
        />
      ),
    },
    ...(subscription?.hasAccessToChecklists
      ? [
          {
            id: "checklist",
            group: "lists",
            component: (
              <IconButton
                src="/assets/editor-toolbar/checklist.svg"
                onClick={() => editor.chain().focus().toggleTaskList().run()}
                className={classnames({ active: formatState.taskList, "toolbar-icon": true, "list-icon": true })}
                ariaLabel="Checklist"
                aria-pressed={formatState.taskList}
                hoverTipText="Checklist"
              />
            ),
          },
        ]
      : []),
    {
      id: "paragraph",
      group: "headings",
      component: (
        <IconButton
          src="/assets/editor-toolbar/Aa.svg"
          onClick={(e) => editor.chain().focus().setParagraph().run()}
          className={classnames({ active: isBody, "toolbar-icon": true })}
          ariaLabel="Paragraph"
          aria-pressed={isBody}
          hoverTipText="Paragraph"
        />
      ),
    },
    {
      id: "h1",
      group: "headings",
      component: (
        <IconButton
          src={`/assets/editor-toolbar/h1.svg`}
          onClick={(e) => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          className={classnames({ active: formatState.h1, "toolbar-icon": true })}
          ariaLabel="Heading 1"
          aria-pressed={formatState.h1}
          hoverTipText="Heading 1"
        />
      ),
    },
    {
      id: "h2",
      group: "headings",
      component: (
        <IconButton
          src={`/assets/editor-toolbar/h2.svg`}
          onClick={(e) => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={classnames({ active: formatState.h2, "toolbar-icon": true })}
          ariaLabel="Heading 2"
          aria-pressed={formatState.h2}
          hoverTipText="Heading 2"
        />
      ),
    },
    {
      id: "h3",
      group: "headings",
      component: (
        <IconButton
          src={`/assets/editor-toolbar/h3.svg`}
          onClick={(e) => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={classnames({ active: formatState.h3, "toolbar-icon": true })}
          ariaLabel="Heading 3"
          aria-pressed={formatState.h3}
          hoverTipText="Heading 3"
        />
      ),
    },
    // Keep the cleanup action at the right edge of the formatting controls.
    // Only offered when a non-empty document has something to clean (blank
    // paragraphs, leading/trailing hardBreaks, or double-hardBreak spacing).
    ...(editor && hasExtraSpacing(editor.state.doc)
      ? [
          {
            id: "cleanUpSpacing",
            group: "spacing",
            component: (
              <span style={{ position: "relative" }}>
                <StickyNoteAnchor anchorId={AnchorIds.COLLAPSE_EMPTY_PARAGRAPHS_BUTTON} />
              <SudoMenuButton
                open={showRemoveEmptyLinesMenu}
                onOpenChange={(open) => {
                  if (!open && isSchedulingProjectCleanup) return;
                  setShowRemoveEmptyLinesMenu(open);
                }}
                trigger={
                  <span style={{ display: "inline-flex" }}>
                    <IconButton
                      src="/assets/editor-toolbar/collapse-empty-paragraphs.svg"
                      onClick={(event) => {
                        event.stopPropagation();
                        setShowRemoveEmptyLinesMenu((open) => !open);
                      }}
                      className="toolbar-icon"
                      ariaLabel="Remove empty lines"
                      aria-haspopup="menu"
                      aria-expanded={showRemoveEmptyLinesMenu}
                      hoverTipText="Remove empty lines"
                      disableTooltip={showRemoveEmptyLinesMenu}
                    />
                  </span>
                }
                side="bottom"
                align="start"
                sideOffset={4}
                collisionPadding={8}
                menuAriaLabel="Remove empty lines scope"
              >
                <MenuPopoverItem
                  className="whitespace-nowrap"
                  onSelect={() => {
                    const removed = removeExtraSpacing(editor);
                    sendAppEvent("Clean Up Spacing Clicked", { documentId, removed });
                  }}
                >
                  In Document
                </MenuPopoverItem>
                <MenuPopoverItem
                  className="whitespace-nowrap"
                  disabled={isSchedulingProjectCleanup || hasProjectCleanup}
                  closeOnSelect={false}
                  onSelect={() => void scheduleProjectCleanup()}
                >
                  {isSchedulingProjectCleanup
                    ? "Scheduling…"
                    : project?.spacingCleanupJob?.state === "running"
                      ? "In Project — in progress"
                      : hasProjectCleanup
                        ? "In Project — results available"
                        : "In Project"}
                </MenuPopoverItem>
              </SudoMenuButton>
              </span>
            ),
          },
        ]
      : []),
  ];

  const visibleItemCount = getVisibleToolbarItemCount(rightToolbarItems, formatToolbarWidth);
  const visibleItems = rightToolbarItems.slice(0, visibleItemCount);
  const hiddenItems = rightToolbarItems.slice(visibleItemCount);

  useEffect(() => {
    if (hiddenItems.length === 0) {
      setShowHiddenItems(false);
    }
  }, [hiddenItems.length]);

  const renderToolbarItems = (items) => items.map((item, index) => (
    <li
      key={item.id}
      data-toolbar-id={item.id}
      className={classnames({ "group-start": startsToolbarGroup(items, index) })}
    >
      {item.component}
    </li>
  ));

  const projectHasOutlineContent = project?.useStructuredOutline ? structuredOutline?.content?.items?.length > 0 : outlineObject?.sections?.length > 0;
  const displaySectionSelector = storyBibleActive && projectHasOutlineContent;

  // No waiting for Y.Doc readiness; we attempt transform immediately and fallback on error

  const yDocFallbackSnapshot = async (editor) => {
    const snapshotContent = { default: editor.getJSON() };
    await createDocumentSnapshot(documentId, snapshotContent);
  };

  const handleOpenScenesHistory = useCallback(async () => {
    // Open modal immediately for good UX
    setShowHistory(true);
    setIsCreatingSnapshot(true);

    try {
      // Prefer yDoc if available; fallback to editor JSON on any error
      const yDocRef = ydoc || editor?.storage?.collaboration?.document || editor?.extensionStorage?.collaboration?.document;
      if (yDocRef) {
        try {
          const snapshotContent = TiptapTransformer.fromYdoc(yDocRef);
          await createDocumentSnapshot(documentId, snapshotContent);
        } catch (_err) {
          await yDocFallbackSnapshot(editor);
        }
      } else {
        await yDocFallbackSnapshot(editor);
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          feature: "modal-document-history-snapshot-creation",
          documentId,
        },
      });
    } finally {
      setIsCreatingSnapshot(false);
    }
  }, [documentId, editor, ydoc]);

  useEffect(() => {
    const onOpenDocumentHistory = () => {
      void handleOpenScenesHistory();
    };
    window.addEventListener("sudo-open-document-history", onOpenDocumentHistory);
    return () => window.removeEventListener("sudo-open-document-history", onOpenDocumentHistory);
  }, [handleOpenScenesHistory]);

  if (!editor) {
    return null;
  }

  return (
    <>
      <StyledContainer
        className={classnames("sticky-toolbar", { leftBarCollapsed: leftBarCollapsed, collapsed, "overflow-open": showHiddenItems })}
        ref={editorToolbarRef}
        aria-label="Document Editor Toolbar"
      >
        <ul className="right-container" ref={formatToolbarRef}>
          {renderToolbarItems(visibleItems)}
          {hiddenItems.length > 0 && (
            <li>
              <IconButton
                src="/assets/three-dots.svg"
                className="toolbar-icon hidden-items-menu"
                ariaLabel="More formatting"
                aria-expanded={showHiddenItems}
                onClick={(e) => setShowHiddenItems(!showHiddenItems)}
              />
            </li>
          )}
        </ul>

        {showHiddenItems && (
          <ul className="hidden-items">
            {renderToolbarItems(hiddenItems)}
          </ul>
        )}

        <ul className="left-container">
          {displaySectionSelector && !subscription?.hasAccessToChapterGlueBeta && (
            <li>
              <div>
                <OutlineSectionSelector />
              </div>
            </li>
          )}
          {hasAccessToCleanCopy && (
            <li>
              <CommentCountBadge />
            </li>
          )}

          <li>
            <StickyNoteAnchor anchorId={AnchorIds.STORY_BIBLE_DRAFT_ICON} />
            <IconButton
              id="button-draft-generator-icon"
              className={classnames({ "prose-generator": true, active: proseGeneratorActive, "toolbar-icon": true })}
              onClick={() => {
                sendFlowSignal(TourSignal.STORY_BIBLE_DRAFT_OPENED);
                !proseGeneratorActive && hideLinkingMenu();
                setProseGeneratorActive(!proseGeneratorActive);
                if (!proseGeneratorActive) {
                  setChapterEditorActive(false);
                }
              }}
              src="/assets/story-bible/generator.svg"
              smartFill={false}
              ariaLabel={project?.useScenes ? "Open Draft" : "Open Chapter Generator"}
              hoverTipText={project?.useScenes ? "Draft" : "Chapter Generator"}
            />
          </li>

          <li>
            <IconButton
              className={classnames("history", "toolbar-icon")}
              onClick={handleOpenScenesHistory}
              src="/assets/clock.svg"
              ariaLabel="Open History"
              hoverTipText="Document History"
            />
          </li>

          <li>
            <IconButton
              className={classnames("collapse", "toolbar-icon", { collapsed })}
              onClick={handleCollapseToggle}
              src="/assets/collapse-toolbar.svg"
              ariaLabel="Collapse Toolbar"
              hoverTipText="Collapse Toolbar"
              disableTooltip={isExpanding}
            />
          </li>
        </ul>
      </StyledContainer>
      <History
        close={() => setShowHistory(false)}
        isOpen={showHistory}
        onRestore={handleHistoryRestore}
        documentId={documentId}
        editorComputedStyles={editorComputedStyles}
        isCreatingSnapshot={isCreatingSnapshot}
      />
    </>
  );
}

export default React.memo(EditorToolbar, (prev, next) => {
  return _.isEqual(prev, next);
});
